/**
 * 원본 파일 목록의 append 소스 — 폴더 한 칸을 창(페이지) 단위로 이어 받는다.
 *
 * c6 `ListViewSource` 는 offset 페이징을 말하지만 서버는 **커서**를 준다. 첫 페이지(offset 0)는
 * 커서 없이, 그 뒤는 마지막 응답의 커서로 묻는다 — offset 은 "이어 받기인가" 를 가르는 데만 쓴다.
 * 폴더가 바뀌면 소스를 새로 만든다(커서는 폴더의 것이다).
 *
 * Owns:
 * - 커서 보관과 페이지 요청의 모양.
 *
 * Does not own:
 * - 누적 · 상태 · 늦은 응답 폐기 — c6 `ListViewSource`. wire 해석 — `api/datasetFileApi.ts`.
 */
import {
  ListViewSource,
  type ListViewPageRequest,
  type ListViewPageResponse,
} from "@carbon6/c6-frontend-design";
import { listSourceFiles } from "../api/datasetFileApi";
import type { SourceFile } from "../domain/sourceFile";

/** 한 창의 항목 수 — 화면 한 판보다 넉넉하되 수십만 개짜리 폴더에서도 첫 화면이 빠르게. */
export const SOURCE_FILE_PAGE_SIZE = 100;

export type SourceFileListSourceOptions = {
  datasetUuid: string;
  /** root 기준 폴더 경로. 빈 문자열이면 root. */
  parent: string;
  resolveErrorMessage: (error: unknown) => string;
};

export class SourceFileListSource extends ListViewSource<SourceFile> {
  private readonly datasetUuid: string;
  private readonly parent: string;
  private cursor: string | null = null;

  constructor({ datasetUuid, parent, resolveErrorMessage }: SourceFileListSourceOptions) {
    super({ pageSize: SOURCE_FILE_PAGE_SIZE, resolveErrorMessage });
    this.datasetUuid = datasetUuid;
    this.parent = parent;
  }

  protected override async onLoadPage(
    request: ListViewPageRequest,
  ): Promise<ListViewPageResponse<SourceFile>> {
    const cursor = request.offset === 0 ? undefined : (this.cursor ?? undefined);
    const page = await listSourceFiles({
      datasetUuid: this.datasetUuid,
      parent: this.parent,
      cursor,
      limit: request.size,
    });
    this.cursor = page.cursor;
    return { items: page.entries, hasMore: page.cursor !== null };
  }

  protected override getItemKey(item: SourceFile): string {
    return item.path;
  }
}
